import React, { useState, useEffect } from 'react';
import { supabase } from '../../lib/supabase';
import Modal from '../../components/ui/Modal';
import FamilyChain from '../../components/ui/FamilyChain';
import { fmtDate, initials } from '../../lib/constants';
import toast from 'react-hot-toast';

const ROLE_ICON  = { head:'👨', spouse:'👩', child:'👧', member:'👤' };
const STATUS_BG  = { active:'var(--success-bg)', inactive:'#ECEFF1', deceased:'#ECEFF1', pending:'var(--warning-bg)' };
const STATUS_CLR = { active:'var(--success)',     inactive:'#546E7A', deceased:'#90A4AE', pending:'#E65100' };

export default function HouseholdsPage() {
  const [households,  setHouseholds]  = useState([]);
  const [districts,   setDistricts]   = useState([]);
  const [loading,     setLoading]     = useState(true);
  const [distF,       setDistF]       = useState('');
  const [search,      setSearch]      = useState('');
  const [viewH,       setViewH]       = useState(null);   // household detail modal
  const [editH,       setEditH]       = useState(null);   // edit modal
  const [showAdd,     setShowAdd]     = useState(false);
  const [saving,      setSaving]      = useState(false);
  const [members,     setMembers]     = useState([]);     // members of viewed household

  const blank = { name: '', district_id: '' };
  const [form, setForm] = useState({ ...blank });

  useEffect(() => { loadAll(); }, []);

  async function loadAll() {
    setLoading(true);
    const [{ data: fams }, { data: dists }] = await Promise.all([
      supabase
        .from('families')
        .select(`
          id, name, district_id,
          districts(id, name),
          members(id, first_name, last_name, family_role, membership_type,
                  status, phone, gender, dob, districts(name))
        `)
        .order('name'),
      supabase.from('districts').select('*').order('name'),
    ]);
    setHouseholds(fams || []);
    setDistricts(dists || []);
    setLoading(false);
  }

  async function loadMembers(householdId) {
    const { data } = await supabase
      .from('members')
      .select('*, districts(name)')
      .eq('family_id', householdId)
      .order('family_role');
    setMembers(data || []);
  }

  function openView(h) {
    setViewH(h);
    loadMembers(h.id);
  }

  function openEdit(h) {
    setForm({ name: h.name, district_id: h.district_id || '' });
    setEditH(h);
  }

  async function handleSave(e) {
    e.preventDefault(); setSaving(true);
    const payload = { name: form.name.trim(), district_id: form.district_id || null };

    if (editH) {
      const { error } = await supabase.from('families').update(payload).eq('id', editH.id);
      if (error) toast.error(error.message);
      else { toast.success('Household updated ✅'); setEditH(null); loadAll(); }
    } else {
      const { error } = await supabase.from('families').insert(payload);
      if (error) toast.error(error.message);
      else { toast.success('Household created ✅'); setShowAdd(false); setForm({ ...blank }); loadAll(); }
    }
    setSaving(false);
  }

  async function handleDelete(h) {
    const memberCount = (h.members || []).length;
    const msg = memberCount > 0
      ? `Delete "${h.name}"? This household has ${memberCount} member(s). Members will be unlinked but NOT deleted.`
      : `Delete household "${h.name}"? This cannot be undone.`;
    if (!window.confirm(msg)) return;

    // Unlink all members first
    if (memberCount > 0) {
      await supabase.from('members').update({ family_id: null, family_role: 'member' })
        .eq('family_id', h.id);
    }
    const { error } = await supabase.from('families').delete().eq('id', h.id);
    if (error) toast.error(error.message);
    else { toast.success('Household deleted'); loadAll(); }
  }

  // Filter
  const filtered = households.filter(h => {
    const matchDist   = !distF  || h.district_id === distF;
    const matchSearch = !search || h.name.toLowerCase().includes(search.toLowerCase()) ||
      (h.members || []).some(m =>
        `${m.first_name} ${m.last_name}`.toLowerCase().includes(search.toLowerCase())
      );
    return matchDist && matchSearch;
  });

  // Stats
  const totalMembers = households.reduce((s, h) => s + (h.members || []).length, 0);
  const byDistrict   = {};
  households.forEach(h => {
    const d = h.districts?.name || 'Unknown';
    byDistrict[d] = (byDistrict[d] || 0) + 1;
  });
  const maxD = Math.max(...Object.values(byDistrict), 1);

  return (
    <div>
      {/* Header */}
      <div className="flex-between mb-4">
        <div>
          <h1 style={{ fontSize: 20, fontWeight: 800 }}>🏠 Household Management</h1>
          <div style={{ fontSize: 13, color: 'var(--text3)', marginTop: 2 }}>
            Manage family households and linked members
          </div>
        </div>
        <button className="btn btn-primary" onClick={() => { setForm({ ...blank }); setShowAdd(true); }}>
          + Add Household
        </button>
      </div>

      {/* Stats row */}
      <div className="grid-4 mb-4">
        <div className="stat-card"><div className="stat-icon blue">🏠</div><div className="stat-value">{households.length}</div><div className="stat-label">Total Households</div></div>
        <div className="stat-card"><div className="stat-icon green">👥</div><div className="stat-value">{totalMembers}</div><div className="stat-label">Linked Members</div></div>
        <div className="stat-card"><div className="stat-icon blue">📍</div><div className="stat-value">{Object.keys(byDistrict).length}</div><div className="stat-label">Districts Covered</div></div>
        <div className="stat-card"><div className="stat-icon amber">📊</div><div className="stat-value">{totalMembers > 0 ? (totalMembers / Math.max(households.length, 1)).toFixed(1) : 0}</div><div className="stat-label">Avg Members / Household</div></div>
      </div>

      <div className="grid-2 mb-4">
        {/* District breakdown */}
        <div className="card">
          <div className="card-header"><h3>Households by District</h3></div>
          <div className="card-pad">
            <div className="bar-chart">
              {Object.entries(byDistrict).sort((a, b) => b[1] - a[1]).map(([d, c]) => (
                <div key={d} className="bar-row">
                  <span className="bar-label">{d}</span>
                  <div className="bar-track">
                    <div className="bar-fill blue" style={{ width: `${Math.round((c / maxD) * 100)}%` }}>
                      <span className="bar-fill-val">{c}</span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Largest households */}
        <div className="card">
          <div className="card-header"><h3>Largest Households</h3></div>
          <div>
            {[...households]
              .sort((a, b) => (b.members?.length || 0) - (a.members?.length || 0))
              .slice(0, 6)
              .map((h, i) => (
                <div key={h.id} style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '10px 16px', borderBottom: '1px solid var(--border)' }}>
                  <div style={{ width: 28, height: 28, borderRadius: '50%', background: 'var(--blue-light)', color: 'var(--blue-dark)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 12, fontWeight: 800, flexShrink: 0 }}>
                    {i + 1}
                  </div>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontWeight: 700, fontSize: 13 }}>🏠 {h.name}</div>
                    <div style={{ fontSize: 11, color: 'var(--text3)' }}>{h.districts?.name || '—'}</div>
                  </div>
                  <span style={{ fontSize: 13, fontWeight: 700, color: 'var(--blue-dark)' }}>
                    {h.members?.length || 0} members
                  </span>
                </div>
              ))}
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="card mb-4">
        <div className="card-pad" style={{ paddingTop: 10, paddingBottom: 10 }}>
          <div className="flex gap-2 flex-wrap">
            <div className="search-bar" style={{ flex: '1 1 200px' }}>
              🔍
              <input value={search} onChange={e => setSearch(e.target.value)}
                placeholder="Search household name or member name…" />
              {search && (
                <button onClick={() => setSearch('')}
                  style={{ background: 'none', border: 'none', color: 'var(--text3)', cursor: 'pointer', fontSize: 16 }}>✕</button>
              )}
            </div>
            <select className="form-select" style={{ width: 'auto' }} value={distF} onChange={e => setDistF(e.target.value)}>
              <option value="">All Districts</option>
              {districts.map(d => <option key={d.id} value={d.id}>{d.name}</option>)}
            </select>
            <div style={{ fontSize: 12, color: 'var(--text3)', alignSelf: 'center' }}>
              {filtered.length} household{filtered.length !== 1 ? 's' : ''}
            </div>
          </div>
        </div>
      </div>

      {/* Households table */}
      {loading ? (
        <div style={{ textAlign: 'center', padding: 40, color: 'var(--text3)' }}>Loading households…</div>
      ) : (
        <div className="table-wrap">
          <table>
            <thead>
              <tr>
                <th>Household Name</th>
                <th>District</th>
                <th>Members</th>
                <th>Head of House</th>
                <th>Composition</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filtered.length === 0 && (
                <tr>
                  <td colSpan={6} style={{ textAlign: 'center', padding: 32, color: 'var(--text3)' }}>
                    No households found. Click <strong>+ Add Household</strong> to create one.
                  </td>
                </tr>
              )}
              {filtered.map(h => {
                const mems      = h.members || [];
                const head      = mems.find(m => m.family_role === 'head');
                const spouse    = mems.find(m => m.family_role === 'spouse');
                const children  = mems.filter(m => m.family_role === 'child').length;
                const otherMems = mems.filter(m => !['head','spouse','child'].includes(m.family_role)).length;
                const activeCount = mems.filter(m => m.status === 'active').length;

                return (
                  <tr key={h.id}>
                    <td>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                        <div style={{ width: 34, height: 34, borderRadius: 8, background: 'var(--blue-light)', color: 'var(--blue-dark)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 16, flexShrink: 0 }}>
                          🏠
                        </div>
                        <div>
                          <div style={{ fontWeight: 700 }}>{h.name}</div>
                          <div style={{ fontSize: 11, color: 'var(--text3)' }}>{activeCount} active · {mems.length - activeCount} inactive</div>
                        </div>
                      </div>
                    </td>
                    <td>
                      <span style={{ fontSize: 11, padding: '2px 8px', background: '#EDE7F6', color: '#4527A0', borderRadius: 100, fontWeight: 600 }}>
                        {h.districts?.name || '—'}
                      </span>
                    </td>
                    <td>
                      <span style={{ fontSize: 18, fontWeight: 800, color: 'var(--blue-dark)' }}>{mems.length}</span>
                    </td>
                    <td>
                      {head ? (
                        <div>
                          <div style={{ fontWeight: 600, fontSize: 13 }}>👨 {head.first_name} {head.last_name}</div>
                          {spouse && <div style={{ fontSize: 11, color: 'var(--text3)' }}>👩 {spouse.first_name} {spouse.last_name}</div>}
                        </div>
                      ) : <span style={{ color: 'var(--text3)', fontSize: 12 }}>Not assigned</span>}
                    </td>
                    <td>
                      <div style={{ display: 'flex', gap: 5, flexWrap: 'wrap' }}>
                        {head    && <span className="badge badge-blue"  style={{ fontSize: 10 }}>1 Head</span>}
                        {spouse  && <span className="badge badge-blue"  style={{ fontSize: 10 }}>1 Spouse</span>}
                        {children > 0 && <span className="badge badge-green" style={{ fontSize: 10 }}>{children} Child{children > 1 ? 'ren' : ''}</span>}
                        {otherMems > 0 && <span className="badge badge-gray"  style={{ fontSize: 10 }}>{otherMems} Other</span>}
                      </div>
                    </td>
                    <td>
                      <div className="flex gap-1">
                        <button className="btn btn-sm btn-blue" onClick={() => openView(h)}>View</button>
                        <button className="btn btn-sm btn-outline" onClick={() => openEdit(h)}>Edit</button>
                        <button className="btn btn-sm btn-danger" onClick={() => handleDelete(h)}>🗑</button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* ── VIEW HOUSEHOLD MODAL ── */}
      {viewH && (
        <Modal
          title={`🏠 ${viewH.name}`}
          onClose={() => { setViewH(null); setMembers([]); }}
          large
          footer={
            <div style={{ display: 'flex', gap: 8 }}>
              <button className="btn btn-ghost" onClick={() => { setViewH(null); setMembers([]); }}>Close</button>
              <button className="btn btn-outline" onClick={() => { openEdit(viewH); setViewH(null); setMembers([]); }}>✏️ Edit Household</button>
            </div>
          }
        >
          {/* Household info */}
          <div style={{ display: 'flex', gap: 16, marginBottom: 20, flexWrap: 'wrap' }}>
            {[
              ['District', viewH.districts?.name || '—'],
              ['Total Members', members.length],
              ['Active Members', members.filter(m => m.status === 'active').length],
              ['Full Members', members.filter(m => m.membership_type === 'Full Member').length],
            ].map(([l, v]) => (
              <div key={l} style={{ background: 'var(--bg)', borderRadius: 8, padding: '10px 16px', minWidth: 110, textAlign: 'center' }}>
                <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--text3)', textTransform: 'uppercase', letterSpacing: .4, marginBottom: 4 }}>{l}</div>
                <div style={{ fontSize: 18, fontWeight: 800, color: 'var(--blue-dark)' }}>{v}</div>
              </div>
            ))}
          </div>

          {/* Members list */}
          <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--text3)', textTransform: 'uppercase', letterSpacing: .5, marginBottom: 10 }}>
            Household Members ({members.length})
          </div>

          {members.length === 0 ? (
            <div className="alert alert-info">No members linked to this household yet. Edit a member and assign them this family household.</div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 0, border: '1px solid var(--border)', borderRadius: 10, overflow: 'hidden' }}>
              {members.map((m, idx) => (
                <div key={m.id} style={{
                  display: 'flex', alignItems: 'center', gap: 14,
                  padding: '13px 16px',
                  borderBottom: idx < members.length - 1 ? '1px solid var(--border)' : 'none',
                  background: m.status === 'deceased' ? '#FAFAFA' : 'white',
                  opacity: m.status === 'deceased' ? .6 : 1,
                }}>
                  {/* Avatar */}
                  <div style={{
                    width: 38, height: 38, borderRadius: '50%', flexShrink: 0,
                    background: m.status === 'deceased' ? '#ECEFF1' : 'var(--blue-light)',
                    color: m.status === 'deceased' ? '#90A4AE' : 'var(--blue-dark)',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    fontSize: 13, fontWeight: 700,
                  }}>
                    {(m.first_name[0] || '') + (m.last_name[0] || '')}
                  </div>

                  {/* Info */}
                  <div style={{ flex: 1 }}>
                    <div style={{ fontWeight: 700, fontSize: 14, textDecoration: m.status === 'deceased' ? 'line-through' : 'none' }}>
                      {ROLE_ICON[m.family_role] || '👤'} {m.first_name} {m.last_name}
                    </div>
                    <div style={{ fontSize: 11, color: 'var(--text3)', marginTop: 2 }}>
                      {m.family_role?.charAt(0).toUpperCase() + m.family_role?.slice(1) || '—'}
                      {' · '}{m.membership_type}
                      {m.districts?.name && ` · ${m.districts.name}`}
                      {m.phone && ` · 📞 ${m.phone}`}
                      {m.status === 'deceased' && ' · ⚰️ Deceased'}
                    </div>
                  </div>

                  {/* Status badge */}
                  <span style={{
                    fontSize: 11, fontWeight: 700, padding: '3px 10px', borderRadius: 100,
                    background: STATUS_BG[m.status] || '#ECEFF1',
                    color: STATUS_CLR[m.status] || '#546E7A',
                  }}>
                    {m.status}
                  </span>
                </div>
              ))}
            </div>
          )}
        </Modal>
      )}

      {/* ── ADD / EDIT MODAL ── */}
      {(showAdd || editH) && (
        <Modal
          title={editH ? `Edit — ${editH.name}` : 'Add New Household'}
          onClose={() => { setShowAdd(false); setEditH(null); setForm({ ...blank }); }}
          footer={
            <>
              <button className="btn btn-ghost" onClick={() => { setShowAdd(false); setEditH(null); setForm({ ...blank }); }}>Cancel</button>
              <button className="btn btn-primary" form="household-form" type="submit" disabled={saving}>
                {saving ? 'Saving…' : editH ? 'Save Changes' : 'Create Household'}
              </button>
            </>
          }
        >
          <form id="household-form" onSubmit={handleSave}>
            <div className="form-group">
              <label className="form-label">Household Name *</label>
              <input className="form-input"
                value={form.name}
                onChange={e => setForm(f => ({ ...f, name: e.target.value }))}
                placeholder="e.g. Kariuki Family, Njoroge Household"
                required />
              <div style={{ fontSize: 11, color: 'var(--text3)', marginTop: 4 }}>
                Usually the family surname followed by "Family" or "Household".
              </div>
            </div>
            <div className="form-group">
              <label className="form-label">District</label>
              <select className="form-select"
                value={form.district_id}
                onChange={e => setForm(f => ({ ...f, district_id: e.target.value }))}>
                <option value="">Select district…</option>
                {districts.map(d => <option key={d.id} value={d.id}>{d.name}</option>)}
              </select>
            </div>

            {editH && (
              <div className="alert alert-info" style={{ fontSize: 12 }}>
                💡 To add or remove members from this household, go to <strong>Members → Edit Member</strong> and change their Family Household assignment.
              </div>
            )}
          </form>
        </Modal>
      )}
    </div>
  );
}
